import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { exportEnvironmentData, fetchCompetitions, getGroups } from '../../../api/index.js';
import EnvironmentExportFilters from './EnvironmentExportFilters.js';
import EnvironmentExportPreview from './EnvironmentExportPreview.js';
import ExportConfirmationDialog from './ExportConfirmationDialog.js';

const normalizeProviders = (payload) => {
  const rawValues = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.groups)
    ? payload.groups
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return [...new Set(
    rawValues
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.provider || item?.name || item?.value || item?.label || item?.group || '';
      })
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));
};

const EnvironmentExport = () => {
  const [providerOptions, setProviderOptions] = useState([]);
  const [providerCompetitions, setProviderCompetitions] = useState([]);

  const [sourceProvider, setSourceProvider] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);

  const [providersLoading, setProvidersLoading] = useState(false);
  const [competitionsLoading, setCompetitionsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [error, setError] = useState('');
  const [exportResult, setExportResult] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadProviders = async () => {
      setProvidersLoading(true);
      try {
        const response = await getGroups();
        if (!mounted) return;
        setProviderOptions(normalizeProviders(response?.data));
      } catch (providerError) {
        // Les champs sont freeSolo : l'admin peut toujours saisir les providers manuellement.
        if (mounted) setProviderOptions([]);
      } finally {
        if (mounted) setProvidersLoading(false);
      }
    };

    loadProviders();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    setDiscipline('');
    setSelectedSeasons([]);
    setSelectedCompetitions([]);
    setProviderCompetitions([]);
    setExportResult(null);
    setError('');

    if (!sourceProvider) return undefined;

    const loadCompetitions = async () => {
      setCompetitionsLoading(true);
      try {
        const response = await fetchCompetitions({ provider: sourceProvider });
        if (!mounted) return;
        setProviderCompetitions(Array.isArray(response?.data) ? response.data : []);
      } catch (competitionError) {
        if (!mounted) return;
        setError(
          competitionError?.response?.data?.message ||
            competitionError?.message ||
            'Impossible de charger les compétitions pour ce provider.'
        );
      } finally {
        if (mounted) setCompetitionsLoading(false);
      }
    };

    // Le provider source est freeSolo : on évite un appel API à chaque frappe.
    const timeoutId = window.setTimeout(loadCompetitions, 400);

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [sourceProvider]);

  useEffect(() => {
    setSelectedSeasons([]);
    setSelectedCompetitions([]);
    setExportResult(null);
  }, [discipline]);

  const disciplineOptions = useMemo(
    () =>
      [...new Set(providerCompetitions.map((competition) => competition?.discipline).filter(Boolean))]
        .map(String)
        .sort((a, b) => a.localeCompare(b)),
    [providerCompetitions]
  );

  const seasonOptions = useMemo(() => {
    if (!discipline) return [];

    return [
      ...new Set(
        providerCompetitions
          .filter(
            (competition) =>
              String(competition?.discipline || '') === String(discipline)
          )
          .map((competition) => competition?.season)
          .filter(Boolean)
          .map(String)
      ),
    ].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [providerCompetitions, discipline]);

  const competitionOptions = useMemo(() => {
    if (!discipline) return [];

    return providerCompetitions.filter((competition) => {
      const sameDiscipline =
        String(competition?.discipline || '') === String(discipline);

      const sameSeason =
        selectedSeasons.length === 0 ||
        selectedSeasons.includes(String(competition?.season || ''));

      return sameDiscipline && sameSeason;
    });
  }, [providerCompetitions, discipline, selectedSeasons]);

  const handleSeasonsChange = (seasons) => {
    const normalizedSeasons = [...new Set((seasons || []).map(String))];

    setSelectedSeasons(normalizedSeasons);
    setSelectedCompetitions((currentCompetitions) =>
      currentCompetitions.filter(
        (competition) =>
          normalizedSeasons.length === 0 ||
          normalizedSeasons.includes(String(competition?.season || ''))
      )
    );
    setExportResult(null);
  };

  const hasValidDateRange = !startDate || !endDate || startDate <= endDate;

  const canSubmit = Boolean(
    sourceProvider &&
      discipline &&
      selectedCompetitions.length > 0 &&
      hasValidDateRange &&
      !competitionsLoading &&
      !exportLoading
  );

  const handleOpenConfirmation = () => {
    if (!canSubmit) return;
    setError('');
    setConfirmationOpen(true);
  };

  const handleConfirmExport = async () => {
    const payload = {
      sourceProvider,
      discipline,
      startDate: startDate || null,
      endDate: endDate || null,
      competitionIds: selectedCompetitions.map((competition) => String(competition._id)),
    };

    setExportLoading(true);
    setError('');

    try {
      const response = await exportEnvironmentData(payload);
      setExportResult(response?.data || null);
      setConfirmationOpen(false);
    } catch (exportError) {
      setError(
        exportError?.response?.data?.message ||
          exportError?.response?.data?.error ||
          exportError?.message ||
          "Une erreur est survenue pendant la préparation de l'export."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownload = () => {
    if (!exportResult || exportResult?.valid === false) return;

    const snapshot = JSON.stringify(exportResult, null, 2);
    const blob = new Blob([snapshot], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const safeSource = sourceProvider.replace(/[^a-z0-9_-]/gi, '-');
    const safeDiscipline = discipline.replace(/[^a-z0-9_-]/gi, '-');

    anchor.href = url;
    anchor.download = `environment-export-${safeSource}-${safeDiscipline}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Export environnement
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Prépare un snapshot cohérent de compétitions, équipes, joueurs, matchs et faits de jeu afin de le réimporter dans un autre environnement.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <EnvironmentExportFilters
          sourceProvider={sourceProvider}
          discipline={discipline}
          selectedSeasons={selectedSeasons}
          startDate={startDate}
          endDate={endDate}
          providerOptions={providerOptions}
          disciplineOptions={disciplineOptions}
          seasonOptions={seasonOptions}
          competitionOptions={competitionOptions}
          selectedCompetitions={selectedCompetitions}
          competitionsLoading={competitionsLoading}
          providersLoading={providersLoading}
          canSubmit={canSubmit}
          onSourceProviderChange={setSourceProvider}
          onDisciplineChange={setDiscipline}
          onSeasonsChange={handleSeasonsChange}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onCompetitionsChange={setSelectedCompetitions}
          onSubmit={handleOpenConfirmation}
        />

        {exportLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Préparation et validation du snapshot...</Typography>
          </Box>
        )}

        <EnvironmentExportPreview result={exportResult} onDownload={handleDownload} />
      </Stack>

      <ExportConfirmationDialog
        open={confirmationOpen}
        loading={exportLoading}
        sourceProvider={sourceProvider}
        discipline={discipline}
        selectedSeasons={selectedSeasons}
        startDate={startDate}
        endDate={endDate}
        competitions={selectedCompetitions}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmExport}
      />
    </Box>
  );
};

export default EnvironmentExport;
